import noop from 'lodash/noop';
import React, { useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { ModalBody, ModalBodyProps, ModalComponentName } from '@components';
import { Button, ButtonProps } from '@elements/button';
import { Text, TextProps } from '@elements/text';
import { TextWithMd, TextWithMdProps } from '@elements/text/textWithMd';
import { Description, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { closeModalSelector, useModalStore } from '@store';
import { sendAnalyticsEvent, sluggify, testProps } from '@utils';

export interface ModalProps {
  isOpen?: boolean;
  titleProps?: TextProps;
  centreAlign?: boolean;
  bodyTextProps?: TextWithMdProps;
  body?: ModalBodyProps<ModalComponentName>;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  bgColor?: 'snow' | 'mint' | 'charcoal';
  ImageTitle?: React.ReactNode;
  Footer?: React.ReactNode;
  showCloseButton?: boolean;
  showButtonsInColumn?: boolean;
  actions?: ButtonProps[];
  blurOverlay?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  onClose?: () => Promise<unknown> | void;
  sendExitEvent?: boolean;
  bodyContainerClassName?: string;
  actionContainerClassName?: string;
  children?: React.ReactNode;
  shouldDisableAutoFocus?: boolean;
}

const sizeClassNames = {
  sm: '!w-96',
  md: '!w-[530px]',
  lg: '!w-[768px]',
  xl: '!w-[1024px]',
};

const bgColorClassNames = {
  snow: 'bg-cabo-50',
  mint: 'bg-nusa-200',
  charcoal: 'bg-fuji-800-100',
};

export const Modal: React.FC<ModalProps> = ({
  bodyTextProps,
  titleProps,
  centreAlign = false,
  body,
  Footer,
  ImageTitle = null,
  size = 'sm',
  showCloseButton = true,
  actionContainerClassName,
  actions,
  shouldCloseOnOverlayClick = true,
  bodyContainerClassName = 'mt-4 p-4 sm:p-6',
  onClose,
  sendExitEvent,
  children,
  bgColor = 'snow',
  isOpen,
  blurOverlay = true,
}) => {
  const closeModal = useModalStore(closeModalSelector);
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  const handleClose = async () => {
    await onClose?.();
    if (sendExitEvent) {
      sendAnalyticsEvent('Modal exited');
    }
    closeModal();
  };

  return (
    <Dialog
      initialFocus={initialFocusRef}
      open={isOpen}
      as="div"
      className="relative z-10"
      onClose={shouldCloseOnOverlayClick ? handleClose : noop}>
      <div className={twMerge('fixed inset-0', blurOverlay && 'bg-black/60 ')} />
      <div
        {...testProps('modal-body')}
        className={twMerge('fixed inset-0 z-10', 'overflow-y-auto')}>
        <span ref={initialFocusRef} className="hidden"></span>
        <div className="flex items-end  justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            className={twMerge(
              sizeClassNames[size],
              'relative w-full transform rounded-2xl !bg-mono-100 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-6xl',
              bgColorClassNames[bgColor],
            )}>
            {showCloseButton && (
              <div className="absolute top-0 right-0 pt-s2 pr-s2">
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="tertiary"
                  icon="close"
                  className="text-icon-brand-2"
                  {...testProps('modal-close-button')}
                />
              </div>
            )}
            {ImageTitle}
            <div
              className={twMerge(
                'space-y-s8 ',
                bodyContainerClassName,
                centreAlign && 'text-center',
              )}>
              {titleProps && <DialogTitle as={Text} variant="h4-24/sb" {...titleProps} />}
              <div>
                {bodyTextProps ? (
                  <Description
                    as={TextWithMd}
                    variant="body-16/r"
                    {...testProps('modal-description-with-display-text-id')}
                    {...bodyTextProps}
                  />
                ) : null}
                {React.isValidElement(body) ? (
                  body
                ) : (
                  <ModalBody {...(body as ModalBodyProps<ModalComponentName>)} />
                )}
              </div>
            </div>
            {Footer
              ? Footer
              : actions && (
                  <div
                    className={twMerge(
                      'p-s24 sm:flex sm:justify-between sm:space-x-4',
                      actionContainerClassName,
                    )}>
                    {actions.map((buttonProps, index) => (
                      <Button
                        className={twMerge('w-full mb-2 md:mb-0')}
                        key={index}
                        {...buttonProps}
                        onClick={async event => {
                          await buttonProps?.onClick?.(event);
                          closeModal();
                        }}
                        {...testProps(
                          `modal-button-${sluggify(buttonProps.title ?? index.toString())}`,
                        )}
                      />
                    ))}
                  </div>
                )}
          </DialogPanel>
        </div>
      </div>
      {children}
    </Dialog>
  );
};
